import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { EnumToken, CountryToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useTaxCategoryMutations } from '~/hooks/use-tax-categories';
import { taxCategorySchema, EMPTY_TAX_CATEGORY, type TaxCategoryFormValues } from '~/lib/schemas/tax-category';

const NO_COUNTRY = '__none__';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initial: TaxCategoryFormValues | null;
	taxTypes: EnumToken[];
	taxStatuses: EnumToken[];
	countries: CountryToken[];
}

/** Add / edit a tax category (Κατηγορία ΦΠΑ). */
export function TaxCategoryEditDialog({ open, onOpenChange, initial, taxTypes, taxStatuses, countries }: Props) {
	const { t } = useTranslation();
	const { create, update } = useTaxCategoryMutations();
	const form = useForm<TaxCategoryFormValues>({
		resolver: zodResolver(taxCategorySchema) as Resolver<TaxCategoryFormValues>,
		defaultValues: initial ?? EMPTY_TAX_CATEGORY,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, formState } = form;
	const isEdit = Boolean(initial?.id);

	useEffect(() => {
		if (open) reset(initial ?? EMPTY_TAX_CATEGORY);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync(values);
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	const tt = watch('tt');
	const ts = watch('ts');
	const ci = watch('ci');
	const visible = watch('iv');

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('prices.taxCategories.editTitle') : t('prices.taxCategories.addTitle')}</DialogTitle>
					<DialogDescription>{t('prices.taxCategories.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<FormField label={t('prices.taxCategories.columns.name')} error={formState.errors.nm?.message}>
						<Input {...register('nm')} maxLength={100} aria-invalid={Boolean(formState.errors.nm)} autoFocus />
					</FormField>
					<div className="grid grid-cols-2 gap-4">
						<FormField label={t('prices.taxCategories.columns.type')} error={formState.errors.tt?.message}>
							<Select value={String(tt)} onValueChange={v => setValue('tt', Number(v), { shouldDirty: true })}>
								<SelectTrigger className="w-full" aria-label={t('prices.taxCategories.columns.type')}>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{taxTypes.map(o => (
										<SelectItem key={o.id} value={String(o.id)}>
											{o.nm}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</FormField>
						<FormField label={t('prices.taxCategories.columns.status')} error={formState.errors.ts?.message}>
							<Select value={String(ts)} onValueChange={v => setValue('ts', Number(v), { shouldDirty: true })}>
								<SelectTrigger className="w-full" aria-label={t('prices.taxCategories.columns.status')}>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{taxStatuses.map(o => (
										<SelectItem key={o.id} value={String(o.id)}>
											{o.nm}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</FormField>
					</div>
					<FormField label={t('prices.taxCategories.columns.country')}>
						<Select
							value={ci === null ? NO_COUNTRY : String(ci)}
							onValueChange={v => setValue('ci', v === NO_COUNTRY ? null : Number(v), { shouldDirty: true })}>
							<SelectTrigger className="w-full" aria-label={t('prices.taxCategories.columns.country')}>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value={NO_COUNTRY}>{t('prices.taxCategories.noCountry')}</SelectItem>
								{countries.map(c => (
									<SelectItem key={c.id} value={String(c.id)}>
										{c.nm}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</FormField>
					<div className="grid grid-cols-3 gap-4">
						<FormField label={t('prices.taxCategories.columns.rate')} error={formState.errors.rt?.message}>
							<Input type="number" step="any" {...register('rt', { valueAsNumber: true })} aria-invalid={Boolean(formState.errors.rt)} />
						</FormField>
						<FormField label={t('prices.taxCategories.columns.sort')} error={formState.errors.si?.message}>
							<Input type="number" {...register('si', { valueAsNumber: true })} aria-invalid={Boolean(formState.errors.si)} />
						</FormField>
						<div className="flex items-end">
							<div className="flex w-full items-center justify-between rounded-md border border-border px-4 py-2">
								<Label htmlFor="tax-cat-visible">{t('prices.taxCategories.columns.visible')}</Label>
								<Switch id="tax-cat-visible" checked={visible} onCheckedChange={next => setValue('iv', next, { shouldDirty: true })} />
							</div>
						</div>
					</div>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
